import {
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MediaService } from '@classifieds-sell/datalayer';
import { Subject } from 'rxjs';
import { isNullOrWhiteSpace, isValidUrl } from '@classifieds-sell/shared';
import { DraftFacade } from '@classifieds-sell/state';
import { PropertySectionBase } from '../../property-section-base';

@Component({
    selector: 'property-3d-tour-section',
    templateUrl: './property-3d-tour-section.component.html',
    styleUrls: ['./property-3d-tour-section.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class Property3DTourSectionComponent
    extends PropertySectionBase
    implements OnInit, OnDestroy {
    private _destroyed$: Subject<void> = new Subject<void>();

    add3DTourText = 'Add 3D tour';
    remove3DTourText = 'Remove 3D tour';
    invalidUrl = 'Sorry, this is not a valid URL';
    invalidProviderErrorMessage =
        'Sorry, we don’t currently support this 3D tour provider. Please add the URL to the property description';
    invalidUrlErrorMessage =
        'Sorry, that URL is not valid. Check the URL and try again';

    inputDisabled = false;
    buttonTitle = this.add3DTourText;

    isProcessing = false;
    hasError = false;
    errorMessage = '';

    safeUrl: SafeResourceUrl;
    originalUrl: string;
    previewUrl = false;

    @ViewChild('property3DTourSectionForm')
    property3DTourSectionForm: NgForm;

    constructor(
        draftFacade: DraftFacade,
        private _changeDetectorRef: ChangeDetectorRef,
        private _sanitizer: DomSanitizer,
        private _mediaService: MediaService
    ) {
        super(draftFacade);
    }

    ngOnDestroy(): void {
        super.onDestroy();
        this._destroyed$.next();
    }

    ngOnInit(): void {
        super.onInit(() => {
            if (
                (this.draftData?.propertyPhotos?.threeDTourUrl || '').length >
                    0 &&
                this.isValidProvider()
            ) {
                const url = this.draftData?.propertyPhotos?.threeDTourUrl;
                const safeUrl =
                    this._sanitizer.bypassSecurityTrustResourceUrl(url);
                if (this.originalUrl !== url) {
                    this.safeUrl = safeUrl;
                    this.originalUrl =
                        this.draftData?.propertyPhotos?.threeDTourUrl;

                    if (!this.previewUrl) {
                        this.previewUrl = true;
                    }
                }
                this.inputDisabled = true;
                this.buttonTitle = this.remove3DTourText;
            } else {
                this.inputDisabled = false;
                this.buttonTitle = this.add3DTourText;
            }
            this._changeDetectorRef.markForCheck();
        });
    }

    isValid(): boolean {
        return true; // allow user to progress through form even if the url is broken
    }

    dirty(): boolean {
        if (this.property3DTourSectionForm.form.disabled) {
            return this.property3DTourSectionForm.form.controls.threeDTourUrl
                .dirty;
        }
        return this.property3DTourSectionForm.form.dirty;
    }

    isValidProvider(): boolean {
        const regex = new RegExp(
            this.validation.propertyPhotos.threeDTourUrl.validations.regularExpression
        );

        const url = this.draftData.propertyPhotos.threeDTourUrl;
        if (!isNullOrWhiteSpace(url)) {
            return regex.test(url);
        }

        return true;
    }

    validateAndProcess(): void {
        const url = this.draftData.propertyPhotos.threeDTourUrl;
        if (!isNullOrWhiteSpace(url)) {
            if (!isValidUrl(url)) {
                this.property3DTourSectionForm?.form?.controls?.threeDTourUrl.setErrors(
                    { invalidUrl: true }
                );
                this.hasError = true;
                this.errorMessage = this.invalidUrl;
                this.isProcessing = false;
                return;
            }

            if (this.isValidProvider()) {
                this._mediaService
                    .validate3DTourUrl(url)
                    .subscribe((response) => {
                        if (response.isValid) {
                            this.updateState();
                        } else {
                            if (response.errorCode === 'INVALID_URL') {
                                this.property3DTourSectionForm?.form?.controls?.threeDTourUrl.setErrors(
                                    { invalidUrl: true }
                                );
                                this.errorMessage = this.invalidUrlErrorMessage;
                            } else {
                                this.property3DTourSectionForm?.form?.controls?.threeDTourUrl.setErrors(
                                    { invalidProvier: true }
                                );
                                this.errorMessage =
                                    this.invalidProviderErrorMessage;
                            }
                            this.hasError = true;
                        }
                        this.isProcessing = false;
                        this._changeDetectorRef.markForCheck();
                    });
            } else {
                this.property3DTourSectionForm?.form?.controls?.threeDTourUrl.setErrors(
                    { invalidProvier: true }
                );
                this.hasError = true;
                this.errorMessage = this.invalidProviderErrorMessage;
                this.isProcessing = false;
            }
        }
        this.safeUrl = null;
        this.previewUrl = false;
        this._changeDetectorRef.markForCheck();
    }

    on3DTourButtonClick(): void {
        this.hasError = false;
        if (this.buttonTitle === this.add3DTourText) {
            this.isProcessing = true;
            this.validateAndProcess();
        } else if (this.buttonTitle === this.remove3DTourText) {
            this.draftData.propertyPhotos.threeDTourUrl = null;
            this.safeUrl = null;
            this.originalUrl = null;
            this.previewUrl = false;
            this.updateState();
        }
        this._changeDetectorRef.markForCheck();
    }

    onInputChange(value: string) {
        if (isNullOrWhiteSpace(value)) {
            this.hasError = false;
        }
    }

    updateState() {
        this.property3DTourSectionForm.form.markAllAsTouched();
        this.property3DTourSectionForm.form.markAsDirty();
        this.publish();
    }
}
